import { Component, OnInit, inject, NgZone, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { map, filter, startWith, shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { LoggerService } from '@app/core/infrastructure';
import { AuthFacade } from '@app/store/auth/auth.facade';
import { UiFacade } from '@app/store/ui';
import { PUBLIC_ROUTES } from '@app/store/router/router.selectors';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    private readonly logger = inject(LoggerService).scope('AppComponent');

    isSidenavOpen = signal(false);
    isAuthenticated$!: Observable<boolean>;
    isLoggedIn$!: Observable<boolean>;
    isGlobalLoading$!: Observable<boolean>;
    isPublicRoute$!: Observable<boolean>;

    constructor(
        private ngZone: NgZone,
        private authFacade: AuthFacade,
        private router: Router,
        private uiFacade: UiFacade,
    ) {}

    ngOnInit() {
        this.isAuthenticated$ = this.authFacade.isAuthenticated$;
        this.isLoggedIn$ = this.authFacade.isAuthenticated$;
        this.isGlobalLoading$ = this.uiFacade.globalLoading$;

        // Initialize NgRx auth state
        this.authFacade.checkInitialAuth();

        // Track if we're on a public route (login, register, etc.)
        this.isPublicRoute$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => PUBLIC_ROUTES.some(route => this.router.url.startsWith(route))),
            startWith(PUBLIC_ROUTES.some(route => this.router.url.startsWith(route))),
            distinctUntilChanged(),
            shareReplay(1)
        );

        // Subscribe to sidenav state from UI store
        this.uiFacade.sidenavOpen$
            .pipe(untilDestroyed(this))
            .subscribe(isOpen => {
                this.isSidenavOpen.set(isOpen);
            });

        // Close sidenav on logout
        this.authFacade.isAuthenticated$
            .pipe(untilDestroyed(this))
            .subscribe(isAuthenticated => {
                if (!isAuthenticated) {
                    this.uiFacade.closeSidenav();
                }
            });

        // Initialize deep link handling for OAuth redirects
        this.initializeDeepLinkHandling();

        // Listen for in-app browser dismissal on mobile
        if (Capacitor.isNativePlatform()) {
            Browser.addListener('browserFinished', () => {
                this.ngZone.run(() => {
                    this.logger.debug('In-app browser dismissed');
                    if (this.router.url === '/login') {
                        this.authFacade.clearOAuthLoadingState();
                    }
                });
            });
        }
    }

    private initializeDeepLinkHandling(): void {
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            this.ngZone.run(() => {
                this.logger.debug('Deep link received:', event.url);

                if (Capacitor.isNativePlatform()) {
                    Browser.close().catch((err: any) => {
                        this.logger.debug('Browser close failed:', err);
                    });
                }

                if (event.url.includes('/auth/callback')) {
                    let queryParams = '';
                    if (event.url.includes('?')) {
                        queryParams = event.url.substring(event.url.indexOf('?'));
                    }
                    this.router.navigateByUrl('/auth/callback' + queryParams);
                    return;
                }

                try {
                    if (event.url.startsWith('my.greatday.app://')) {
                        const path = event.url.replace('my.greatday.app://', '/');
                        this.router.navigateByUrl(path);
                        return;
                    }
                    const url = new URL(event.url);
                    this.router.navigateByUrl(url.pathname + url.search + url.hash);
                } catch (error) {
                    this.logger.error('Error parsing deep link URL:', error);
                    this.router.navigate(['/home']);
                }
            });
        });
    }

    toggleSidenav(): void {
        this.uiFacade.toggleSidenav();
    }

    closeSidenav(): void {
        this.uiFacade.closeSidenav();
    }
}
